
 import React from "react";
 import { PackageX, RotateCcw, Package, Search, Star, Shield, Building2, Store, Filter, Factory, Wrench, Settings, Cog, Cpu, Truck, Zap, Droplet, PaintBucket, Layers, Gem, Mountain, Hammer } from "lucide-react";
 import { Product, SiteSettings } from "../types";
 import ProductCard from "./ProductCard";
 
 interface CatalogProps {
   isLandingMode?: boolean;
   setCurrentTab: (tab: string) => void;
   products: Product[];
   settings: SiteSettings;
   currency: string;
   onAddToQuote: (product: Product) => void;
   quoteCart: any[];
   searchQuery: string;
   setSearchQuery: (query: string) => void;
   selectedCategory: string;
   selectedOrigin: string;
   sortBy: string;
   setSelectedCategory: (cat: string) => void;
   setSelectedOrigin: (org: string) => void;
   setSortBy: (sort: string) => void;
   appLanguage: "fa" | "en";
 }
 
 export default function Catalog({
   isLandingMode,
   setCurrentTab,
   products,
   settings,
   currency,
   onAddToQuote,
   quoteCart,
   searchQuery,
   setSearchQuery,
   selectedCategory,
   selectedOrigin,
   sortBy,
   setSelectedCategory,
   setSelectedOrigin,
   setSortBy,
   appLanguage,
 }: CatalogProps) {
 
   const handleClearFilters = () => {
     setSelectedCategory("همه");
     setSelectedOrigin("همه");
     setSortBy("default");
     setSearchQuery("");
   };
 
   // Filter Logic matching paints and abrasives
   const filteredProducts = products.filter((p) => {
    let matchesCategory = selectedCategory === "همه" || p.category.includes(selectedCategory);
    if (!matchesCategory && settings.subcategories?.[selectedCategory]) {
      matchesCategory = settings.subcategories[selectedCategory].some(sub => p.category.includes(sub));
    }
     const matchesOrigin = selectedOrigin === "همه" || (p.specs && p.specs.origin === selectedOrigin);
     
     const query = searchQuery.toLowerCase().trim();
     const matchesSearch =
       query === "" ||
       p.name.toLowerCase().includes(query) ||
       (p.specs?.nameEn && p.specs.nameEn.toLowerCase().includes(query)) ||
       p.sku.toLowerCase().includes(query) ||
       p.description.toLowerCase().includes(query) ||
       (p.specs?.descEn && p.specs.descEn.toLowerCase().includes(query)) ||
       p.category.some(c => 
         c.toLowerCase().includes(query) || 
         (settings.categoriesEn?.[c] && settings.categoriesEn[c].toLowerCase().includes(query))
       ) ||
       (p.specs && p.specs.brand && (
         p.specs.brand.toLowerCase().includes(query) ||
         (settings.brandsEn?.[p.specs.brand] && settings.brandsEn[p.specs.brand].toLowerCase().includes(query))
       ));
 
     return matchesCategory && matchesOrigin && matchesSearch;
   });
 
   // Sorting Logic
   const sortedProducts = [...filteredProducts].sort((a, b) => {
     if (sortBy === "price-asc") {
       return (a.price || Infinity) - (b.price || Infinity);
     }
     if (sortBy === "price-desc") {
       return (b.price || 0) - (a.price || 0);
     }
     if (sortBy === "name-asc") {
       return a.name.localeCompare(b.name);
     }
     if (sortBy === "sku") {
       return a.sku.localeCompare(b.sku);
     }
     return 0; // default
   });
 
   const dynCategories = React.useMemo(() => {
     if (settings.categories && settings.categories.length > 0) return settings.categories;
     return Array.from(new Set(products.flatMap(p => p.category))).filter(Boolean);
   }, [settings.categories, products]);
 
   // Determine which categories to show on the landing page
   const landingCategories = settings.featuredCategories || [];
 
   const isCategorizedGridActive = isLandingMode && settings.enableCategoryGrids;
   const isAlmas = import.meta.env.VITE_TENANT_ID === "almas";
   const featuredProducts = products.filter(p => p.isFeatured);

   // Helper to render icon by name
   const renderCategoryIcon = (catName: string) => {
     const iconName = settings.categoryIcons?.[catName] || "Package";
     const iconProps = { className: "w-4 h-4" };
     
     switch(iconName) {
       case "Factory": return <Factory {...iconProps} />;
       case "Wrench": return <Wrench {...iconProps} />;
       case "Settings": return <Settings {...iconProps} />;
       case "Cog": return <Cog {...iconProps} />;
       case "Cpu": return <Cpu {...iconProps} />;
       case "Truck": return <Truck {...iconProps} />;
       case "Zap": return <Zap {...iconProps} />;
       case "Droplet": return <Droplet {...iconProps} />;
       case "PaintBucket": return <PaintBucket {...iconProps} />;
       case "Layers": return <Layers {...iconProps} />;
       case "Shield": return <Shield {...iconProps} />;
       case "Gem": return <Gem {...iconProps} />;
       case "Mountain": return <Mountain {...iconProps} />;
       case "Hammer": return <Hammer {...iconProps} />;
       default: return <Package {...iconProps} />;
     }
   };
 
   return (
     <div id="catalog-root" className={`max-w-7xl mx-auto px-4 ${isLandingMode ? 'py-10' : 'py-8'}`} style={{ direction: appLanguage === "fa" ? "rtl" : "ltr" }}>          
       {!isLandingMode && (
         <div className="flex flex-col lg:flex-row gap-8 items-start">
           {/* Desktop Sidebar Filters */}
           <aside className="w-full lg:w-72 shrink-0 bg-white border border-slate-200 rounded-2xl shadow-sm p-5 space-y-6 lg:sticky lg:top-24 hidden lg:block">
             <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
               <Filter className={`w-5 h-5 ${isAlmas ? "text-blue-600" : "text-indigo-600"}`} />
               <h3 className="font-black text-slate-800 text-sm">{appLanguage === "en" ? "Advanced Filters" : "فیلترهای پیشرفته"}</h3>
             </div>
             
             <div className="space-y-3">
               <h4 className="font-bold text-slate-700 text-xs">{appLanguage === "en" ? "Search" : "جستجو"}</h4>
               <div className="relative">
                 <input
                   type="text"
                   value={searchQuery}
                   onChange={(e) => setSearchQuery(e.target.value)}
                   placeholder={appLanguage === "en" ? "Search products..." : "نام کالا، کد یا برند..."}
                   className={`w-full text-xs px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none transition-colors ${isAlmas ? "focus:border-blue-500" : "focus:border-indigo-500"}`}
                 />
                 <Search className="w-4 h-4 text-slate-400 absolute top-1/2 -translate-y-1/2" style={{ right: appLanguage === "fa" ? 'auto' : '0.75rem', left: appLanguage === "fa" ? '0.75rem' : 'auto' }} />
               </div>
             </div>
 
             <div className="space-y-3">
               <h4 className="font-bold text-slate-700 text-xs">{appLanguage === "en" ? "Categories" : "دسته‌بندی‌ها"}</h4>
               <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                 <label className="flex items-center gap-2 cursor-pointer group">
                   <input type="radio" name="cat" checked={selectedCategory === "همه"} onChange={() => setSelectedCategory("همه")} className={`w-4 h-4 border-slate-300 ${isAlmas ? "text-blue-600 focus:ring-blue-500" : "text-indigo-600 focus:ring-indigo-500"}`} />
                   <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900">{appLanguage === "en" ? "All Categories" : "همه دسته‌بندی‌ها"}</span>
                 </label>
                 {dynCategories.map(cat => (
                  <div key={cat} className="flex flex-col gap-1.5 mt-2">
                    <label className="flex items-center gap-2 cursor-pointer group">
                      <input type="radio" name="cat" checked={selectedCategory === cat} onChange={() => setSelectedCategory(cat)} className={`w-4 h-4 border-slate-300 ${isAlmas ? "text-blue-600 focus:ring-blue-500" : "text-indigo-600 focus:ring-indigo-500"}`} />
                      <span className="text-xs font-bold text-slate-700 group-hover:text-slate-900">{appLanguage === "en" ? (settings.categoriesEn?.[cat] || cat) : cat}</span>
                    </label>
                    {(settings.subcategories?.[cat] || []).map(sub => (
                      <label key={sub} className="flex items-center gap-2 cursor-pointer group pr-5">
                        <input type="radio" name="cat" checked={selectedCategory === sub} onChange={() => setSelectedCategory(sub)} className={`w-3 h-3 border-slate-300 ${isAlmas ? "text-blue-600 focus:ring-blue-500" : "text-indigo-600 focus:ring-indigo-500"}`} />
                        <span className="text-[11px] font-medium text-slate-500 group-hover:text-slate-800">{appLanguage === "en" ? (settings.categoriesEn?.[sub] || sub) : sub}</span>
                      </label>
                    ))}
                  </div>
                 ))}
               </div>
             </div>
 
             <div className="space-y-3">
               <h4 className="font-bold text-slate-700 text-xs">{appLanguage === "en" ? "Origin" : "کشور مبدا"}</h4>
               <select value={selectedOrigin} onChange={(e) => setSelectedOrigin(e.target.value)} className={`w-full text-xs px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none transition-colors cursor-pointer ${isAlmas ? "focus:border-blue-500" : "focus:border-indigo-500"}`} dir={appLanguage === "en" ? "ltr" : "rtl"}>
                 <option value="همه">{appLanguage === "en" ? "All Origins" : "همه کشورها"}</option>
                 {(settings.origins || ["ایران", "آلمان", "چین", "ایتالیا", "هند", "ترکیه"]).map(org => (
                   <option key={org} value={org}>{appLanguage === "en" ? (settings.originsEn?.[org] || org) : org}</option>
                 ))}
               </select>
             </div>
 
             <div className="space-y-3">
               <h4 className="font-bold text-slate-700 text-xs">{appLanguage === "en" ? "Sort By" : "مرتب‌سازی بر اساس"}</h4>
               <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`w-full text-xs px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none transition-colors cursor-pointer ${isAlmas ? "focus:border-blue-500" : "focus:border-indigo-500"}`}>
                 <option value="default">{appLanguage === "en" ? "Default" : "پیش‌فرض"}</option>
                 <option value="price-desc">{appLanguage === "en" ? "Highest Price" : "گران‌ترین"}</option>
                 <option value="price-asc">{appLanguage === "en" ? "Lowest Price" : "ارزان‌ترین"}</option>
                 <option value="name-asc">{appLanguage === "en" ? "Alphabetical (A-Z)" : "الفبا (A-Z)"}</option>
               </select>
             </div>
 
             {(selectedCategory !== "همه" || selectedOrigin !== "همه" || sortBy !== "default" || searchQuery !== "") && (
               <button onClick={handleClearFilters} className="w-full py-2.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-xl text-xs font-bold transition-colors flex items-center justify-center gap-1.5 cursor-pointer">
                 <RotateCcw className="w-4 h-4" />
                 <span>{appLanguage === "en" ? "Clear Filters" : "حذف تمامی فیلترها"}</span>
               </button>
             )}
           </aside>
 
           {/* Main Store Content */}
           <div className="flex-1 w-full min-w-0">
             <div className="flex items-center gap-3 mb-6 border-b border-slate-200 pb-4">
               <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border shadow-sm ${isAlmas ? "bg-blue-50 border-blue-100/50" : "bg-indigo-50 border-indigo-100/50"}`}>
                 <Package className={`w-6 h-6 ${isAlmas ? "text-blue-600" : "text-indigo-600"}`} />
               </div>
               <div>
                   <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tight">{appLanguage === "en" ? "Store Catalog" : "فروشگاه و کاتالوگ جامع"}</h2>
                 <p className="text-[11px] md:text-xs text-slate-500 font-medium mt-1">
                   {appLanguage === "en" ? "Showing" : "نمایش"} <span className={`font-extrabold px-1 ${isAlmas ? "text-blue-600" : "text-indigo-600"}`}>{sortedProducts.length}</span> {appLanguage === "en" ? "products" : "نوع محصول در مجموعه"}
                   {searchQuery && <span className="mr-2">| {appLanguage === "en" ? "Results for:" : "نتایج برای:"} <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-100 font-mono">"{searchQuery}"</span></span>}
                 </p>
               </div>
             </div>
             
             {/* Products Grid */}
             {sortedProducts.length === 0 ? (
               <div className="bg-white border border-slate-200 rounded-3xl py-16 px-4 text-center text-slate-500 max-w-2xl mx-auto space-y-5 shadow-sm">
                 <PackageX className="w-16 h-16 mx-auto text-indigo-300 animate-pulse" />
                 <div className="space-y-1.5">
                   <h3 className="font-black text-slate-800 text-base">{appLanguage === "en" ? "No products found!" : "محصولی با فیلترهای فعلی یافت نشد!"}</h3>
                   <p className="text-xs text-slate-400">{appLanguage === "en" ? "Adjust your search or category filters and try again." : "گزینه‌های دسته‌بندی یا جستجوی خود را تغییر داده و مجدداً تلاش کنید."}</p>
                 </div>
                 <button
                   onClick={handleClearFilters}
                   className={`px-6 py-3 text-white rounded-xl text-xs font-bold transition-all mx-auto inline-flex items-center justify-center gap-2 cursor-pointer shadow-lg ${isAlmas ? "bg-gradient-to-l from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 shadow-blue-500/25" : "bg-gradient-to-l from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 shadow-indigo-500/25"}`}
                 >
                   <RotateCcw className="w-4 h-4" />
                   <span>{appLanguage === "en" ? "Clear Filters & View All" : "پاک کردن فیلترها و مشاهده همه"}</span>
                 </button>
               </div>
             ) : (
               <div id="products-grid" className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                 {sortedProducts.map((p) => {
                   const isInCart = quoteCart.some((item) => item.id === p.id);
                   return (
                     <ProductCard
                       key={p.id}
                       product={p}
                       currency={currency}
                       onAddToQuote={onAddToQuote}
                       isInQuote={isInCart}
                       appLanguage={appLanguage}
                      enableQuoteCart={settings.enableQuoteCart !== false}
                      settings={settings}
                     />
                   );
                 })}
               </div>
             )}
           </div>
         </div>
       )}
       
       {isCategorizedGridActive ? (
         <div className="space-y-16 animate-fadeIn pb-12">
           
           {/* 1. Featured Products Slider (محصولات ویژه و شگفت‌انگیز) */}
           {featuredProducts.length > 0 && (
             <div className="space-y-5">
               <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-200 pb-3">
                 <div className="flex items-center gap-2">
                   <div className={`w-10 h-10 rounded-xl flex items-center justify-center border shadow-sm ${isAlmas ? "bg-cyan-50 border-cyan-100 text-cyan-600" : "bg-amber-50 border-amber-100 text-amber-500"}`}>
                     <Star className="w-5 h-5" fill="currentColor" />
                   </div>
                   <h3 className="text-xl md:text-2xl font-black text-slate-800">{appLanguage === "en" ? "Featured & Best Sellers" : "محصولات ویژه و پرفروش"}</h3>
                 </div>
               </div>
               <div className="-mx-4 px-4 sm:mx-0 sm:px-2 flex overflow-x-auto snap-x snap-mandatory gap-4 md:gap-6 pb-8 pt-2 custom-scrollbar">
                 {featuredProducts.slice(0, 10).map((p) => {
                   const isInCart = quoteCart.some((item) => item.id === p.id);
                   return (
                     <div key={p.id} className="snap-start shrink-0 w-[260px] sm:w-[280px] md:w-[320px]">
                       <ProductCard
                         product={p}
                         currency={currency}
                         onAddToQuote={onAddToQuote}
                         isInQuote={isInCart}
                         appLanguage={appLanguage}
                        enableQuoteCart={settings.enableQuoteCart !== false}
                        settings={settings}
                       />
                     </div>
                   );
                 })}
               </div>
             </div>
           )}
 
           {/* 2. Category Carousels (اسلایدر دسته‌بندی‌ها) */}
           {landingCategories.map(cat => {
             const catProducts = products.filter(p => p.category.includes(cat)).slice(0, 8);
             if (catProducts.length === 0) return null;
             
             return (
               <div key={cat} className="space-y-4">
                 <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 border-b border-slate-200 pb-3">
                   <div className="flex items-center gap-2">
                     <div className={`w-8 h-8 rounded-lg flex items-center justify-center border shadow-sm ${isAlmas ? "bg-blue-50 border-blue-100 text-blue-600" : "bg-indigo-50 border-indigo-100 text-indigo-600"}`}>
                       {renderCategoryIcon(cat)}
                     </div>
                     <h3 className="text-lg md:text-xl font-black text-slate-800">{cat}</h3>
                   </div>
                   <button
                     onClick={() => {
                       setSelectedCategory(cat);
                       setCurrentTab("store");
                       window.scrollTo({ top: 0, behavior: "smooth" });
                     }}
                     className={`px-4 py-2.5 bg-slate-100 text-slate-600 text-xs font-bold rounded-xl transition-colors cursor-pointer border border-slate-200 ${isAlmas ? "hover:bg-blue-50 hover:text-blue-700" : "hover:bg-indigo-50 hover:text-indigo-700"}`}
                   >
                     {appLanguage === "en" ? "View all products" : "مشاهده همه محصولات این دسته"}
                   </button>
                 </div>
                 <div className="-mx-4 px-4 sm:mx-0 sm:px-2 flex overflow-x-auto snap-x snap-mandatory gap-4 md:gap-6 pb-8 pt-2 custom-scrollbar">
                   {catProducts.map((p) => {
                     const isInCart = quoteCart.some((item) => item.id === p.id);
                     return (
                       <div key={p.id} className="snap-start shrink-0 w-[260px] sm:w-[280px] md:w-[320px]">
                         <ProductCard
                           key={p.id}
                           product={p}
                           currency={currency}
                           onAddToQuote={onAddToQuote}
                           isInQuote={isInCart}
                           appLanguage={appLanguage}
                          enableQuoteCart={settings.enableQuoteCart !== false}
                          settings={settings}
                         />
                       </div>
                     );
                   })}
                 </div>
               </div>
             );
           })}
 
           {/* 3. Brands Section (برندهای همکار) */}
           {(settings.partnerBrands || settings.brands || []).length > 0 && (
             <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-200 shadow-sm text-center mt-8">
               <div className="w-12 h-12 bg-slate-50 border border-slate-150 rounded-2xl flex items-center justify-center mx-auto mb-4">
                 <Shield className="w-6 h-6 text-slate-400" />
               </div>
               <h3 className="text-xl md:text-2xl font-black text-slate-800 mb-8">
                 {appLanguage === "en" 
                   ? (settings.partnerBannerTitleEn || "Trusted Brands & Partners") 
                   : (settings.partnerBannerTitle || "برندهای معتبر و شرکای تجاری")}
               </h3>
               <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                 {(settings.partnerBrands || settings.brands || []).map(b => (
                   <div key={b} className="px-6 py-3.5 bg-slate-50 text-slate-500 font-bold rounded-xl border border-slate-150 grayscale hover:grayscale-0 hover:bg-white hover:text-slate-900 hover:border-slate-300 hover:shadow-md transition-all cursor-default">
                     {b}
                   </div>
                 ))}
               </div>
             </div>
           )}
 
           {/* 4. Store Big CTA Banner (بنر ورود به فروشگاه) */}
           <div className={`rounded-3xl p-8 md:p-14 text-center text-white shadow-xl relative overflow-hidden mt-8 ${isAlmas ? "bg-gradient-to-br from-slate-900 to-[#0b1120]" : "bg-gradient-to-br from-slate-900 to-slate-800"}`}>
             <div className={`absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 opacity-30 ${isAlmas ? "bg-cyan-500" : "bg-indigo-500"}`}></div>
             <div className={`absolute bottom-0 left-0 w-48 h-48 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2 opacity-20 ${isAlmas ? "bg-blue-500" : "bg-violet-500"}`}></div>
             
             <h2 className="text-2xl md:text-4xl font-black mb-4 relative z-10">{appLanguage === "en" ? "Browse Full Catalog" : "مشاهده کاتالوگ کامل محصولات"}</h2>
             <p className="text-slate-300 max-w-2xl mx-auto mb-8 relative z-10 text-sm md:text-base leading-relaxed font-medium">
               {appLanguage === "en" ? "Explore our entire range of machinery, tools, and materials with advanced filtering." : "برای مشاهده تمامی دستگاه‌ها، ابزارآلات و فیلتر کردن دقیق محصولات بر اساس قیمت و دسته‌بندی، وارد فروشگاه تخصصی ما شوید."}
             </p>
             <button onClick={() => { setCurrentTab("store"); window.scrollTo({ top: 0, behavior: "smooth" }); }} className={`px-8 py-4 text-white rounded-xl font-bold text-sm shadow-lg transition-all relative z-10 inline-flex items-center gap-2 cursor-pointer hover:-translate-y-1 ${isAlmas ? "bg-blue-600 hover:bg-blue-500 shadow-blue-500/30" : "bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/30"}`}>
               <Store className="w-5 h-5"/>
               {appLanguage === "en" ? "Enter Store" : "ورود به فروشگاه آنلاین"}
             </button>
           </div>
 
         </div>
       ) : null}
     </div>
   );
 }
